import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Typography,
  Button,
  Box,
  Tooltip,
  Autocomplete,
  IconButton,
  Breadcrumbs,
  Link,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../../../layout/Layout";
import {
  proformaAPI,
  vendorAPI,
  projectMasterAPI,
  manageCompaniesAPI,
  statusMasterAPI,
  handleHrMasterAPI,
} from "../../../config";

function ProformaOrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [proformaNo, setProformaNo] = useState("");
  const [proformaDate, setProformaDate] = useState("");

  // Dropdowns lists
  const [companyList, setCompanyList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [classifyList, setClassifyList] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [siteEngineers, setSiteEngineers] = useState([]);

  // Form Fields
  const [companyId, setCompanyId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [orderType, setOrderType] = useState("1");
  const [termsOfPayment, setTermsOfPayment] = useState("");
  const [dispatchThrough, setDispatchThrough] = useState("");
  const [quotationNo, setQuotationNo] = useState("");
  const [packingCharge, setPackingCharge] = useState(0);
  const [purchaseClassify, setPurchaseClassify] = useState("");
  const [siteEngId, setSiteEngId] = useState("");
  const [siteEngContact, setSiteEngContact] = useState("");
  const [validTo, setValidTo] = useState("");
  const [deliveryScope, setDeliveryScope] = useState("");
  const [termsHeading, setTermsHeading] = useState("");
  const [terms, setTerms] = useState("");
  const [status, setStatus] = useState("Draft");

  // Transportation
  const [transportAmount, setTransportAmount] = useState(0);
  const [transportGst, setTransportGst] = useState(0);
  const [transportCharge, setTransportCharge] = useState(0);

  // Line items
  const [items, setItems] = useState([]);

  // Totals
  const [totalWithoutGst, setTotalWithoutGst] = useState(0);
  const [totalCgst, setTotalCgst] = useState(0);
  const [totalSgst, setTotalSgst] = useState(0);
  const [totalIgst, setTotalIgst] = useState(0);
  const [totalWithGst, setTotalWithGst] = useState(0);

  // Fetch initial dropdown metadata
  useEffect(() => {
    // Fetch companies
    axios.post(manageCompaniesAPI, { intType: 3 }).then((res) => {
      if (res.data.success) setCompanyList(res.data.result);
    });

    // Fetch vendors
    axios.post(vendorAPI, { intType: 3 }).then((res) => {
      if (res.data.success) setVendorList(res.data.result);
    });

    // Fetch projects
    axios.post(projectMasterAPI, { intType: 5 }).then((res) => {
      if (res.data.success) setProjectList(res.data.result);
    });

    // Fetch classification / terms codes from statusMaster
    axios.post(statusMasterAPI, { intType: 14, intParentId: 46 }).then((res) => {
      if (res.data.success) setClassifyList(res.data.result);
    });
    axios.post(statusMasterAPI, { intType: 14, intParentId: 42 }).then((res) => {
      if (res.data.success) setPaymentTermsList(res.data.result);
    });

    // Fetch site engineers
    axios.post(handleHrMasterAPI, { intType: 12 }).then((res) => {
      if (res.data.success) setSiteEngineers(res.data.result);
    });
  }, []);

  // Fetch Proforma Order Details
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios({
      method: "POST",
      url: proformaAPI,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.token,
      },
      data: {
        intType: 2,
        proformaId: id,
      },
    })
      .then((res) => {
        if (res.data.success && res.data.result.length > 0) {
          const detail = res.data.result[0];
          const lineItems = res.data.result[1] || [];

          setProformaNo(detail.proforma_no);
          setProformaDate(detail.proforma_date ? detail.proforma_date.split("T")[0] : "");
          setCompanyId(detail.company_name_id || "");
          setVendorId(detail.vendor_id || "");
          setProjectId(detail.project_id || "");
          setOrderType(detail.order_type || "1");
          setTermsOfPayment(detail.terms_of_payment || "");
          setDispatchThrough(detail.dispatch_through || "");
          setQuotationNo(detail.quotation_no || "");
          setPackingCharge(parseFloat(detail.packing_charge || 0));
          setPurchaseClassify(detail.purchase_classify || "");
          setSiteEngId(detail.site_eng_id || "");
          setSiteEngContact(detail.site_eng_contact || "");
          setValidTo(detail.valid_to ? detail.valid_to.split("T")[0] : "");
          setDeliveryScope(detail.delivery_scope || "");
          setTermsHeading(detail.terms_heading || "");
          setTerms(detail.terms || "");
          setStatus(detail.status || "Draft");

          setTransportAmount(parseFloat(detail.transport_amount || 0));
          setTransportGst(parseFloat(detail.transport_gst || 0));
          setTransportCharge(parseFloat(detail.transport_charge || 0));

          // Map items
          setItems(
            lineItems.map((item) => ({
              id: item.id,
              item_name: item.item_name || "",
              item_description: item.item_description || "",
              hsn_code: item.hsn_code || "",
              quantity: parseFloat(item.quantity || 0),
              unit: item.unit || "",
              rate: parseFloat(item.rate || 0),
              discount: parseFloat(item.discount || 0),
              gst_percentage: parseFloat(item.gst_percentage || 0),
              cgst: parseFloat(item.cgst || 0),
              sgst: parseFloat(item.sgst || 0),
              igst: parseFloat(item.igst || 0),
              total: parseFloat(item.total || 0),
            }))
          );
        }
      })
      .catch((err) => {
        console.error("Failed to load Proforma details:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, userData.token]);

  // Handle updates to Site Engineer
  const handleSiteEngChange = (engId) => {
    setSiteEngId(engId);
    const eng = siteEngineers.find((e) => e.id === engId);
    if (eng) {
      setSiteEngContact(eng.phone_no || "");
    }
  };

  // Recalculate totals dynamically
  useEffect(() => {
    let subtotal = 0;
    let cgstSum = 0;
    let sgstSum = 0;
    let igstSum = 0;

    items.forEach((item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discPct = parseFloat(item.discount) || 0;
      const gstPct = parseFloat(item.gst_percentage) || 0;

      const discountedRate = rate * (1 - discPct / 100);
      const lineSubtotal = qty * discountedRate;
      const lineGst = lineSubtotal * (gstPct / 100);

      subtotal += lineSubtotal;

      // Local vs interstate check ( तमिलनाडु check or igst setting )
      // Use existing values if set, otherwise default to cgst/sgst split
      if (item.igst > 0 || (item.cgst === 0 && item.sgst === 0 && item.igst === 0)) {
        igstSum += lineGst;
      } else {
        cgstSum += lineGst / 2;
        sgstSum += lineGst / 2;
      }
    });

    // Transport calculation
    const transAmt = parseFloat(transportAmount) || 0;
    const transGstVal = transAmt * ((parseFloat(transportGst) || 0) / 100);
    const calculatedTransCharge = transAmt + transGstVal;
    
    setTransportCharge(calculatedTransCharge);

    subtotal += transAmt;
    if (igstSum > 0) {
      igstSum += transGstVal;
    } else {
      cgstSum += transGstVal / 2;
      sgstSum += transGstVal / 2;
    }

    setTotalWithoutGst(subtotal);
    setTotalCgst(cgstSum);
    setTotalSgst(sgstSum);
    setTotalIgst(igstSum);
    
    const finalTotal = subtotal + cgstSum + sgstSum + igstSum + (parseFloat(packingCharge) || 0);
    setTotalWithGst(finalTotal);
  }, [items, transportAmount, transportGst, packingCharge]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        item_name: "",
        item_description: "",
        hsn_code: "",
        quantity: 1,
        unit: "",
        rate: 0,
        discount: 0,
        gst_percentage: 18,
        cgst: 0,
        sgst: 0,
        igst: 0,
        total: 0,
      },
    ]);
  };

  const handleRemoveItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx, field, val) => {
    const list = [...items];
    list[idx][field] = val;
    setItems(list);
  };

  const handleSave = () => {
    setLoading(true);
    axios({
      method: "POST",
      url: proformaAPI,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.token,
      },
      data: {
        intType: 5,
        proformaId: id,
        proforma_no: proformaNo,
        vendor_id: vendorId,
        order_type: orderType,
        company_name_id: companyId,
        project_id: projectId,
        terms_of_payment: termsOfPayment,
        dispatch_through: dispatchThrough,
        quotation_no: quotationNo,
        packing_charge: packingCharge,
        purchase_classify: purchaseClassify,
        site_eng_id: siteEngId,
        site_eng_contact: siteEngContact,
        valid_to: validTo,
        delivery_scope: deliveryScope,
        terms_heading: termsHeading,
        terms: terms,
        transport_amount: transportAmount,
        transport_gst: transportGst,
        transport_charge: transportCharge,
        status: status,
        items: items,
      },
    })
      .then((res) => {
        if (res.data.success) {
          alert(res.data.message);
          navigate("/proforma-order/list");
        }
      })
      .catch((err) => {
        console.error("Save failed:", err);
        alert("Failed to save Proforma Order.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Layout>
      <Box sx={{ p: 3, background: "#f8fafc", minHeight: "100vh" }}>
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6} md={6}>
            <Typography variant="h5" fontWeight="bold">
              Edit Proforma Order ({proformaNo})
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" href="/proforma-order/list">
                Proforma List
              </Link>
              <Typography color="text.primary">Edit</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ borderRadius: "8px" }}
            >
              Back
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              onClick={handleSave}
              sx={{ borderRadius: "8px" }}
            >
              Save Proforma
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Box sx={{ background: "#fff", p: 3, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Company Name"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    fullWidth
                  >
                    {companyList.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.company_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Vendor Name"
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    fullWidth
                  >
                    {vendorList.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    fullWidth
                  >
                    {projectList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Order Type"
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="1">Purchase Order</MenuItem>
                    <MenuItem value="2">Work Order</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Terms of Payment"
                    value={termsOfPayment}
                    onChange={(e) => setTermsOfPayment(e.target.value)}
                    fullWidth
                  >
                    {paymentTermsList.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Dispatch Through"
                    value={dispatchThrough}
                    onChange={(e) => setDispatchThrough(e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Quotation Number"
                    value={quotationNo}
                    onChange={(e) => setQuotationNo(e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Purchase Classification"
                    value={purchaseClassify}
                    onChange={(e) => setPurchaseClassify(e.target.value)}
                    fullWidth
                  >
                    {classifyList.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Site Engineer"
                    value={siteEngId}
                    onChange={(e) => handleSiteEngChange(e.target.value)}
                    fullWidth
                  >
                    {siteEngineers.map((eng) => (
                      <MenuItem key={eng.id} value={eng.id}>
                        {eng.employee_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Site Eng Contact"
                    value={siteEngContact}
                    onChange={(e) => setSiteEngContact(e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    type="date"
                    label="Valid To"
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Delivery Scope"
                    value={deliveryScope}
                    onChange={(e) => setDeliveryScope(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Line items Section */}
          <Grid item xs={12}>
            <Box sx={{ background: "#fff", p: 3, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Items Details
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{ borderRadius: "8px" }}
                >
                  Add Item
                </Button>
              </Box>

              {items.map((item, idx) => (
                <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 2, borderBottom: "1px solid #f1f5f9", pb: 2 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Item Name"
                      value={item.item_name}
                      onChange={(e) => handleItemChange(idx, "item_name", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Description"
                      value={item.item_description}
                      onChange={(e) => handleItemChange(idx, "item_description", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5}>
                    <TextField
                      label="HSN"
                      value={item.hsn_code}
                      onChange={(e) => handleItemChange(idx, "hsn_code", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5}>
                    <TextField
                      type="number"
                      label="Qty"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField
                      label="Unit"
                      value={item.unit}
                      onChange={(e) => handleItemChange(idx, "unit", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5}>
                    <TextField
                      type="number"
                      label="Rate"
                      value={item.rate}
                      onChange={(e) => handleItemChange(idx, "rate", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5}>
                    <TextField
                      type="number"
                      label="Discount %"
                      value={item.discount}
                      onChange={(e) => handleItemChange(idx, "discount", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5}>
                    <TextField
                      type="number"
                      label="GST %"
                      value={item.gst_percentage}
                      onChange={(e) => handleItemChange(idx, "gst_percentage", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton color="error" onClick={() => handleRemoveItem(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Grid>

          {/* Charges and terms */}
          <Grid item xs={12} md={6}>
            <Box sx={{ background: "#fff", p: 3, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", height: "100%" }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Extra Charges & Terms
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label="Transport Amount"
                    value={transportAmount}
                    onChange={(e) => setTransportAmount(parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label="Transport GST %"
                    value={transportGst}
                    onChange={(e) => setTransportGst(parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label="Packing Charge"
                    value={packingCharge}
                    onChange={(e) => setPackingCharge(parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Terms Heading"
                    value={termsHeading}
                    onChange={(e) => setTermsHeading(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Terms Body"
                    multiline
                    rows={4}
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Totals Summary */}
          <Grid item xs={12} md={6}>
            <Box sx={{ background: "#fff", p: 3, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", height: "100%" }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Totals Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography color="text.secondary">Subtotal (No GST):</Typography>
                <Typography fontWeight="bold">₹{totalWithoutGst.toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">CGST:</Typography>
                <Typography>₹{totalCgst.toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">SGST:</Typography>
                <Typography>₹{totalSgst.toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography color="text.secondary">IGST:</Typography>
                <Typography>₹{totalIgst.toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography color="text.secondary">Packing Charge:</Typography>
                <Typography>₹{packingCharge.toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" sx={{ borderTop: "2px solid #f1f5f9", pt: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Grand Total (With GST):
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  ₹{totalWithGst.toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}

export default ProformaOrderEdit;
